<template>
  <b-form @submit.stop.prevent="applyFilter">
    <!-- Advanced search -->
    <template v-if="isAdvancedSearch">
      <b-form-row v-if="enableLocalStorage && savedFilters.length > 0">
        <b-col cols="12" class="py-1" v-if="savedFilters.length > 0">
          <b-button-group
            v-for="(savedFilter, index) in savedFilters"
            :key="index"
            class="mr-1"
          >
            <b-button
              class="btn-sm"
              :variant="savedFilter.isActive ? 'primary' : 'outline-secondary'"
              @click="applySavedFilter(index)"
            >
              {{ savedFilter.name }}
            </b-button>
            <b-button
              class="btn-sm"
              :variant="savedFilter.isActive ? 'primary' : 'outline-secondary'"
              @click="removeFilterFromStorage(index)"
            >
              <base-icon icon="times" />
            </b-button>
          </b-button-group>
        </b-col>
      </b-form-row>

      <b-form-row v-for="(filterQuery, index) in filterQueries" :key="index">
        <b-col v-if="showIsWhereToggle" cols="0">
          <b-form-checkbox
            class="my-2"
            v-if="getFilterQueryType(filterQuery) != Boolean"
            v-model="filterQuery.isWhere"
            name="check-button"
            switch
          />
        </b-col>
        <b-col cols="12" sm="4" lg="3" class="py-1">
          <b-form-select
            value-field="settings"
            text-field="name"
            :options="getAllowedFilterTypes(filterQuery, index)"
            v-model="filterQuery.settings"
            @input="onSelectFilterType(filterQuery)"
          >
          </b-form-select>
        </b-col>
        <b-col cols="8" sm="5" lg="4" class="py-1">
          <template v-if="getFilterQueryType(filterQuery) == Number">
            <b-form-input
              :class="filterQuery.settings.isRange ? 'w-50 d-inline' : 'w-100'"
              v-model="filterQuery.value"
              type="number"
              :placeholder="filterQuery.settings.isRange ? 'From' : ''"
              :required="
                !filterQuery.settings.isRange ||
                filterQuery.settings.requireStart
              "
            />
            <b-form-input
              v-if="filterQuery.settings.isRange"
              class="w-50 d-inline"
              v-model="filterQuery.valueTo"
              type="number"
              placeholder="To"
              :required="filterQuery.settings.requireEnd"
            />
          </template>

          <template v-else-if="getFilterQueryType(filterQuery) == Date">
            <b-container class="py-0">
              <b-row>
                <b-col class="pa-0">
                  <base-date-picker
                    :label="
                      filterQuery.settings.isRange
                        ? $t('common.from')
                        : $t('common.select_date')
                    "
                    label-cols-sm="3"
                    v-model="filterQuery.value"
                    :required="
                      !filterQuery.settings.isRange ||
                      filterQuery.settings.requireStart
                    "
                  />
                </b-col>
              </b-row>
              <b-row>
                <b-col v-if="filterQuery.settings.isRange">
                  <base-date-picker
                    :label="$t('common.to')"
                    label-cols-sm="3"
                    v-model="filterQuery.valueTo"
                    :required="
                      !filterQuery.settings.isRange ||
                      filterQuery.settings.requireEnd
                    "
                  />
                </b-col>
              </b-row>
            </b-container>
          </template>

          <template v-else-if="getFilterQueryType(filterQuery) == Array">
            <base-multiselect
              label
              label-cols-sm="0"
              label-class="p-0"
              v-if="filterQuery.settings.autocomplete"
              v-model="filterQuery.value"
              :settings="{
                label: filterQuery.settings.textField,
                options: filterQuery.settings.isolateOptions
                  ? filterQuery.options || []
                  : filterQuery.settings.options || [],
                multiple: filterQuery.settings.multiple || false,
                trackBy: filterQuery.settings.valueField,
              }"
              :value-field="filterQuery.settings.valueField"
            />
            <template v-else>
              <b-form-select
                :value-field="filterQuery.settings.valueField || 'id'"
                :text-field="filterQuery.settings.textField || 'name'"
                :options="
                  filterQuery.settings.isolateOptions
                    ? filterQuery.options || []
                    : filterQuery.settings.options || []
                "
                v-model="filterQuery.value"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null" disabled>
                    {{
                      filterQuery.settings.placeholder ||
                      $t("common.select_value")
                    }}
                  </b-form-select-option>
                </template>
              </b-form-select>

              <template v-if="filterQuery.settings.child">
                <b-form-select
                  v-if="filterQuery.settings.child.type == Array"
                  :value-field="filterQuery.settings.child.valueField || 'id'"
                  :text-field="filterQuery.settings.child.textField || 'name'"
                  :options="
                    getChildOptions(
                      filterQuery.value,
                      filterQuery.settings.child.parentFieldName,
                      filterQuery.settings.child.options
                    )
                  "
                  v-model="filterQuery.valueChild"
                >
                  <template v-slot:first>
                    <b-form-select-option :value="null" disabled>
                      {{
                        filterQuery.settings.child.placeholder ||
                        $t("common._value")
                      }}
                    </b-form-select-option>
                  </template>
                </b-form-select>

                <b-form-input
                  v-else
                  class="w-100"
                  v-model="filterQuery.valueChild"
                  :placeholder="
                    filterQuery.settings.child.placeholder ||
                    $t('common._value')
                  "
                />
              </template>
            </template>
            <b-overlay :show="filterQuery.isLoading" no-wrap />
          </template>

          <template v-else-if="getFilterQueryType(filterQuery) == Boolean">
            <b-radio-group
              v-model="filterQuery.value"
              :options="
                filterQuery.settings.options || [
                  { text: $t('common.yes'), value: true },
                  { text: $t('common.no'), value: false },
                ]
              "
              buttons
              button-variant="outline-primary"
              :value-field="filterQuery.settings.valueField || 'value'"
              :text-field="filterQuery.settings.textField || 'text'"
              required
              class="d-flex"
            ></b-radio-group>
          </template>

          <b-form-input
            v-else
            class="w-100"
            v-model="filterQuery.value"
            :disabled="!filterQuery.settings.field"
          />
        </b-col>
        <b-col cols="4" md="auto" class="py-1">
          <b-button
            class="btn-sm"
            variant="outline-danger"
            v-if="filterQueries.length > 1"
            @click="removeFilterQuery(index)"
          >
            <base-icon icon="trash-alt" class="text-danger" />
          </b-button>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col v-if="showIsWhereToggle" cols="0">
          <div class="my-2" style="width: 36px"></div>
        </b-col>
        <b-col cols="12" sm="4" lg="3" class="py-1">
          <b-input-group v-if="enableLocalStorage">
            <b-form-input
              v-model="filterStorageName"
              :placeholder="$t('common.filter_name')"
            />
            <b-input-group-append>
              <b-button
                :disabled="!filterStorageName"
                variant="outline"
                @click="addFilterToStorage"
              >
                {{ $t("common.save") }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col cols="12" sm="5" lg="4" class="py-1">
          <b-button-group class="d-flex">
            <b-button variant="outline-secondary" @click="addEmptyFilterQuery">
              {{ $t("common.add") }}
            </b-button>
            <b-button variant="outline-secondary" @click="resetFilter">
              {{ $t("common.reset") }}
            </b-button>
            <b-button variant="outline-success" type="submit">
              {{ $t("common.apply") }}
            </b-button>
          </b-button-group>
        </b-col>
      </b-form-row>
    </template>
    <!-- Simple search -->
    <template v-else-if="filterQueries.length">
      <div style="width: 400px">
        <div class="d-flex">
          <div class="position-relative d-inline flex-grow-1">
            <b-form-input
              :placeholder="$t('common.search_placeholder')"
              v-model="filterQueries[0].value"
            />
            <b-link
              v-if="filterQueries[0].value"
              class="quick-search-clear text-body"
              @click="resetFilter"
            >
              <base-icon icon="times" />
            </b-link>
          </div>
          <b-btn variant="outline-success" type="submit" class="ml-1">
            {{ $t("common.search") }}
          </b-btn>
        </div>
        <div class="text-right p-1" v-if="showAdvancedSearchToggle">
          <b-link class="text-muted text-sm" @click="isAdvancedSearch = true">
            {{ $t("common.advanced_search") }}
          </b-link>
        </div>
      </div>
    </template>
  </b-form>
</template>

<script>
import { hashMixin } from "@foys-tech/foys-utils";
import { BaseMultiselect, BaseDatePicker, BaseIcon } from "./";

/**
##Icons dependencies
Add this font awesome icons to your target project

```js
import {
  faTimes,
  faTrashAlt,
} from '@fortawesome/pro-light-svg-icons';

library.add(
  faTimes,
  faTrashAlt,
);
```


`filterTypes` is array of filter definitions. Filter definition is a js object that describes filter type.

##Base filter type definition

```json
{
  name: "Club location",
  settings: {
    field: "clubLocationId",
    type: Number,
    valueType: Number
  }
},
```
| Name | Description |
| :-- | :-- |
| name | Label shown in the drop down list of the filter types select  |
| settings | Js object describes settings of the filter type  |
| settings.field | Model property that will be set, when filter value is selected/entered   |
| settings.type | Data type of the model property   |
| settings.valueType | Type of value  |
| settings.isRange | If true will show From and To inputs. If false, will show only one input |
| settings.requireStart | is used when isRange = true |
| settings.requireEnd | is used when isRange = true |


###String filter type

```json
{
  name: 'Search',
  settings: {
    field: 'searchString',
    type: String,
  }
}
```

###Number filter type
TODO:  
[ ] add min and max settings?

```json
{
  name: 'Age',
  settings: {
    field: 'age',
    type: Number,
    isRange: false,
    requireStart: true,
    requireEnd: true,
  }
}
```

###Date filter type
TODO:  
[ ] add min and max settings?  
[ ] add requiredStart and requiredEnd for range?

```json
{
  name: 'Date filter',
  settings: {
    field: 'dateFilter',
    type: Date,
    isRange: false,
    requireStart: true,
    requireEnd: true,
  }
},
```

###Select filter type

```json
{
  name: 'Select filter',
  settings: {
    field: 'selectFilter',
    type: Array,
    options: [],
    valueField: 'id',
    textField: 'name',
    placeholder: null,
    child: <filter type>,
    autocomplete: true,

    prepareFn: Function,
    prepareEach: false,

    optionsFn: Function,
    isolateOptions: false,

  }
},
```
| Name | Description |
| :-- | :-- |
| settings.options | array of objects {id,name}  |
| settings.valueField | item property to use as value  |
| settings.textField |  item property to use as label |
| settings.placeholder |  optional label for first option in select |
| settings.child | Filter type that is shown as child (select or search) |
| settings.autocomplete | Use multiselect component with search instead basic select |
| settings.prepareFn | Async function callback is called when filter type is selected. Returned value is not used |
| settings.prepareEach | if true - prepareFn is called every time filter type is selected. false - prepareFn is called only first time filter type is selected |
| settings.optionsFn | Async function callback is called when filter type is selected. Should return array of options that will be used as data source for list |
| settings.isolateOptions | if true - optionsFn will be called for every filter query and use different options. if false - optionsFn will be called only first time filter type is selected and all filter queries of this type will use same options.  |


###Boolean/Switch filter

```json
{
  name: 'Boolean',
  settings: {
    field: 'bool',
    type: Boolean,
    options: [{}],
    textField: '',
    valueField: '',
  }
}
```
| Name | Description |
| :-- | :-- |
| settings.options | defaults to [{ text: 'Yes', value: true }, { text: 'No', value: false }]  |

 */
export default {
  mixins: [hashMixin],
  components: { BaseMultiselect, BaseDatePicker, BaseIcon },
  props: {
    /**
     * Filter unique identifier. Used as key for local storage or url hash.
     */
    filterId: String,
    /**
     * Array of filter definitions.
     */
    filterTypes: {
      type: Array,
      default: () => [],
    },
    /**
     * Async action to load references.
     * For example if any filter definition depends on some data that is loaded via API.
     * Is called (awaited) as first in component mount lifecycle hook.
     */
    loadReferencesFn: null,
    /**
     * Show is where filter toggle.
     */
    showIsWhereToggle: {
      type: Boolean,
      default: false,
    },
    /**
     * Force emit isWhere flag when `showIsWhereToggle` is false.
     */
    emitIsWhereValue: {
      type: Boolean,
      default: false,
    },
    /**
     * Name of the property used for simple search. Should be one of the string filter types in the advanced search.
     */
    simpleSearchField: {
      type: String,
      default: "search",
    },
    /**
     *  Advanced search is enabled by default
     */
    defaultAdvancedSearch: {
      type: Boolean,
      default: true,
    },
    /**
     *  Advanced search is enabled by default
     */
    showAdvancedSearchToggle: {
      type: Boolean,
      default: true,
    },
    /**
     *  Local storage is enabled by default
     */
    enableLocalStorage: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      defaultFilterQuery: {
        settings: {},
        value: null,
        valueTo: null,
        valueChild: null,
        isWhere: true,
      },
      filterQueries: [],
      savedFilters: [],
      storageKey: null,
      filterStorageName: null,
      isAdvancedSearch: false,
    };
  },
  methods: {
    async onSelectFilterType(filterQuery) {
      filterQuery.value = null;
      filterQuery.valueTo = null;
      filterQuery.valueChild = null;
      filterQuery.isWhere = true;

      await this.prepareFilterQuery(filterQuery);
      await this.loadFilterQueryOptions(filterQuery);
    },
    getAllowedFilterTypes(filterQuery, index) {
      return this.filterTypes.filter(
        (e) =>
          (e.settings.multiple && !e.settings.autocomplete) ||
          !this.filterQueries.find(
            (q, i) => i != index && q.settings.field == e.settings.field
          )
      );
    },
    getChildOptions(parentValue, parentFieldName, childOptions) {
      if (parentFieldName) {
        return childOptions.filter((e) => e[parentFieldName] == parentValue);
      }
      return childOptions;
    },
    addEmptyFilterQuery() {
      this.filterQueries.push(Object.assign({}, this.defaultFilterQuery));
    },
    removeFilterQuery(index) {
      this.filterQueries.splice(index, 1);
    },
    getFilterQueryType(filterQuery) {
      if (filterQuery && filterQuery.settings && filterQuery.settings.type) {
        return filterQuery.settings.type;
      }
      return String;
    },

    resetFilter() {
      this.filterQueries = [];
      this.addEmptyFilterQuery();
      this.setDefaultFilterType();
      this.applyFilter();
    },
    setDefaultFilterType() {
      if (this.isAdvancedSearch) {
        let defaultFilterType = this.filterTypes.find(
          (e) => e.settings.isDefault
        );
        if (!defaultFilterType) {
          defaultFilterType = this.filterTypes[0];
        }
        this.filterQueries[0].settings = defaultFilterType.settings;
      } else {
        this.filterQueries[0].settings = this.filterTypes.find(
          (e) => e.settings.field == this.simpleSearchField
        ).settings;
      }
    },
    applyFilter() {
      this.deactivateSavedFilters();
      var filterModel = this.buildFilterModel();
      this.saveToHash(
        this.storageKey,
        this.isAdvancedSearch
          ? { ...filterModel, isAdvancedSearch: true }
          : filterModel
      );
      this.$emit("filterChanged", filterModel);
    },
    buildFilterModel() {
      var model = {};
      this.filterQueries.forEach((f) => {
        if (!f.settings || !f.settings.field) {
          return;
        }
        if (f.value === null && f.settings.initialValue) {
          f.value = f.settings.initialValue;
        }

        if (f.settings.multiple) {
          if (typeof model[f.settings.field] === "undefined") {
            model[f.settings.field] = [];
          }

          if (f.settings.autocomplete) {
            f.value.forEach((v) => {
              let filterQuery = {};
              filterQuery["value"] = v;
              if (this.showIsWhereToggle || this.emitIsWhereValue) {
                filterQuery["isWhere"] = f.isWhere;
              }
              model[f.settings.field].push(filterQuery);
            });
          } else {
            let filterQuery = {};
            if (f.settings.isRange) {
              filterQuery["value.from"] = f.value;
              filterQuery["value.to"] = f.valueTo;
            } else if (f.settings.child) {
              filterQuery["value.parent"] = f.value;
              filterQuery["value.child"] = f.valueChild;
            } else {
              filterQuery["value"] = f.value;
            }
            if (this.showIsWhereToggle || this.emitIsWhereValue) {
              filterQuery["isWhere"] = f.isWhere;
            }
            model[f.settings.field].push(filterQuery);
          }
        } else {
          let filterQuery = {};

          if (f.settings.isRange) {
            filterQuery[f.settings.field + ".from"] = f.value;
            filterQuery[f.settings.field + ".to"] = f.valueTo;
          } else if (f.settings.child) {
            filterQuery[f.settings.field + ".parent"] = f.value;
            filterQuery[f.settings.field + ".child"] = f.valueChild;
          } else {
            filterQuery[f.settings.field] = f.value;
          }
          if (this.showIsWhereToggle || this.emitIsWhereValue) {
            filterQuery[f.settings.field + ".isWhere"] = f.isWhere;
          }
          Object.assign(model, filterQuery);
        }
      });

      let result = {};
      for (const [field = key, filters = value] of Object.entries(model)) {
        if (Array.isArray(filters)) {
          filters.forEach((filter, i) => {
            for (const [prop = key, value] of Object.entries(filter))
              result[`${field}[${i}].${prop}`] = value;
          });
        } else {
          result[field] = filters;
        }
      }

      return result;
    },

    async initFilterTypes(model) {
      if (!model) {
        return;
      }
      for (const [key, value] of Object.entries(model)) {
        var mainKey = key;
        var mainValue = value;
        var mainValueTo = null;
        var mainValueChild = null;
        var mainFilterIsWhere = true;
        if (key.endsWith(".from") || key.endsWith(".to")) {
          mainKey = key.split(".")[0];
          if (mainKey) {
            mainValue = model[mainKey + ".from"];
            mainValueTo = model[mainKey + ".to"];
          }
        }
        if (key.endsWith(".parent") || key.endsWith(".child")) {
          mainKey = key.split(".")[0];
          if (mainKey) {
            mainValue = model[mainKey + ".parent"];
            mainValueChild = model[mainKey + ".child"];
          }
        }
        var filterKey = mainKey;
        mainFilterIsWhere = model[mainKey + ".isWhere"];
        if (mainKey.endsWith("].value")) {
          filterKey = mainKey.substring(0, mainKey.indexOf("]") + 1);
          mainKey = mainKey.substring(0, mainKey.indexOf("["));
          mainFilterIsWhere = model[filterKey + ".isWhere"];
        }

        var existingFilterQuery = this.filterQueries.find(
          (e) => e.settings.field == filterKey
        );

        if (!existingFilterQuery) {
          var filterType = this.filterTypes.find(
            (e) => e.settings.field == mainKey
          );
          if (filterType) {
            if (
              filterType.settings.autocomplete &&
              filterType.settings.multiple
            ) {
              let filterQuery = this.filterQueries.find(
                (e) => e.settings.field == mainKey
              );
              if (filterQuery) {
                filterQuery.value.push(mainValue);
              } else {
                filterQuery = {
                  settings: filterType.settings,
                  value: [mainValue],
                  isWhere: mainFilterIsWhere,
                };
                await this.prepareFilterQuery(filterQuery);
                await this.loadFilterQueryOptions(filterQuery);

                this.filterQueries.push(filterQuery);
              }
            } else {
              let filterQuery = {
                settings: filterType.settings,
                value: mainValue,
                valueTo: mainValueTo,
                valueChild: mainValueChild,
                isWhere: mainFilterIsWhere,
              };

              await this.prepareFilterQuery(filterQuery);
              await this.loadFilterQueryOptions(filterQuery);

              this.filterQueries.push(filterQuery);
            }
          }
        }
      }
    },

    addFilterToStorage() {
      if (!this.enableLocalStorage) {
        return;
      }
      this.savedFilters = this.getFiltersFromStorage();
      var filter = {
        name: this.filterStorageName,
        filter: this.buildFilterModel(),
      };
      this.savedFilters.push(filter);
      this.$storage.set(this.storageKey, this.savedFilters);
      this.filterStorageName = null;
    },
    removeFilterFromStorage(index) {
      if (!this.enableLocalStorage) {
        return;
      }
      this.savedFilters.splice(index, 1);
      this.$storage.set(this.storageKey, this.savedFilters);
    },
    getFiltersFromStorage() {
      if (!this.enableLocalStorage) {
        return [];
      }
      return this.$storage.get(this.storageKey, []);
    },
    applySavedFilter(index) {
      this.deactivateSavedFilters();
      var activeFilter = this.savedFilters[index];
      activeFilter.isActive = true;
      this.$storage.set(this.storageKey, this.savedFilters);

      this.$emit("filterChanged", activeFilter.filter);
    },
    deactivateSavedFilters() {
      if (!this.enableLocalStorage) {
        return;
      }
      this.savedFilters = this.getFiltersFromStorage();
      this.savedFilters.forEach((f) => (f.isActive = false));
      this.$storage.set(this.storageKey, this.savedFilters);
    },

    async prepareFilterQuery(filterQuery) {
      if (typeof filterQuery.settings.prepareFn === "function") {
        if (
          filterQuery.settings.prepareEach ||
          !filterQuery.settings.isPrepared
        ) {
          try {
            filterQuery.isLoading = true;
            await filterQuery.settings.prepareFn(filterQuery);
          } finally {
            filterQuery.isLoading = false;
          }
        }
        if (!filterQuery.settings.prepareEach) {
          filterQuery.settings.isPrepared = true;
        }
      }
    },
    async loadFilterQueryOptions(filterQuery) {
      if (typeof filterQuery.settings.optionsFn === "function") {
        if (filterQuery.settings.isolateOptions) {
          if (!filterQuery.optionsAreLoaded) {
            try {
              filterQuery.isLoading = true;
              let options = await filterQuery.settings.optionsFn();
              this.$set(filterQuery, "options", options);
            } finally {
              filterQuery.isLoading = false;
              filterQuery.optionsAreLoaded = true;
            }
          }
        } else {
          if (!filterQuery.settings.optionsAreLoaded) {
            try {
              filterQuery.isLoading = true;
              let options = await filterQuery.settings.optionsFn();
              this.$set(filterQuery.settings, "options", options);
              filterQuery.settings.optionsAreLoaded = true;
            } finally {
              filterQuery.isLoading = false;
              filterQuery.optionsAreLoaded = true;
            }
          }
        }
      }
    },
  },
  async mounted() {
    this.isAdvancedSearch = this.defaultAdvancedSearch;
    this.savedFilters = this.getFiltersFromStorage();

    let hashData = this.getFromHash();

    if (this.enableHash && hashData && hashData[this.storageKey]) {
      let filterFromHash = hashData[this.storageKey];

      this.isAdvancedSearch = filterFromHash.isAdvancedSearch;
      delete filterFromHash.isAdvancedSearch;

      this.$emit("filterIsSet", filterFromHash);

      if (this.loadReferencesFn) {
        await this.loadReferencesFn();
      }

      await this.initFilterTypes(filterFromHash);

      return;
    }

    if (this.loadReferencesFn) {
      await this.loadReferencesFn();
    }

    this.addEmptyFilterQuery();
    this.setDefaultFilterType();

    var activeFilter = this.savedFilters.find((f) => f.isActive);
    if (activeFilter) {
      this.$emit("filterIsSet", activeFilter.filter);
    } else {
      var model = this.buildFilterModel();
      if (this.enableHash) {
        this.saveToHash(
          this.storageKey,
          this.isAdvancedSearch ? { ...model, isAdvancedSearch: true } : model,
          false
        );
      } else {
        this.$emit("filterIsSet", model);
      }
    }
  },
  created() {
    this.storageKey = `filter_${this.filterId}`;
  },
};
</script>

<style>
.quick-search-clear {
  position: absolute;
  top: 7px;
  right: 13px;
}
</style>